import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { applyBlockStyle, buildContentBlock, createCloseButton, isSafeUrl } from '../block-builder';
import { mountCodeBlock } from '../code-block';
import { buildField } from '../form-content';
import { insertHost, keepHostAttached } from '../inline-host';
import type {
  CompositeStep,
  FormField,
  GuideRenderContext,
  InlinePosition,
  StepBlock,
  StepPosition,
} from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';
import { positionArrow } from './floating-arrow';

/** Lector de un campo de formulario (para los botones submit-forms). */
interface FormReader {
  field: FormField;
  read: () => unknown;
}

/** align de un botón → justify-content de su fila de acciones. */
const ROW_ALIGN: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/**
 * Renderer UNIFICADO del modelo `composite`. Renderiza UN paso componiendo
 * presentación (floating/inline) × posición (9 esquinas / centro /
 * relative-to-element) + diseño + bloques. Reemplaza los 10 renderers por-tipo.
 *
 * El SDK normaliza toda guía a `composite` (`normalizeGuide`) antes de llegar
 * acá, así que este renderer no conoce los tipos viejos.
 *
 * Multi-paso: si `context.nav` está, los botones next/prev/dismiss delegan en
 * las callbacks de navegación (el controller orquesta el avance, igual que en
 * los walkthroughs heterogéneos).
 */
export class CompositeRenderer extends BaseRenderer {
  async render(context: GuideRenderContext): Promise<void> {
    const idx = context.nav?.stepIndex ?? 0;
    const step = context.guide.guideSteps[idx] as unknown as CompositeStep | undefined;
    if (!step) return;

    // Ancla (relative-to-element, inline o BADGE) — sin ella no pintamos.
    const badgeMode = step.badge?.enabled === true;
    const anchorSelector =
      step.position === 'relative-to-element' || step.presentation === 'inline' || badgeMode
        ? step.anchorSelector?.trim() || undefined
        : undefined;
    let anchor: Element | null = null;
    if (anchorSelector) {
      anchor = await waitForElement(anchorSelector);
      if (!anchor) return;
    }

    // DUPLICAR un elemento nativo: una guía INLINE cuyo ÚNICO bloque es un code
    // host se inserta CRUDA en el DOM real (sin shadow ni wrappers), junto al
    // ancla. Así se ve idéntica al elemento de la plataforma (contexto, layout,
    // iconos) — el usuario solo pega el HTML, sin JS.
    const blocks = step.blocks ?? [];
    const soleCode = blocks.length === 1 && blocks[0]?.type === 'code' ? blocks[0] : undefined;
    if (step.presentation === 'inline' && anchor && soleCode && !soleCode.sandboxed) {
      const rawDoc = anchor.ownerDocument ?? document;
      const { cleanup } = mountCodeBlock(soleCode, rawDoc, context, undefined, {
        anchor,
        pos: inlinePosOf(step.inlinePosition),
      });
      this.registerCleanup(cleanup);
      context.onInteraction({ guideId: context.guide.guideId, stepIndex: idx, action: 'shown' });
      return;
    }

    const { host, root } = this.createHost();
    const doc = root.ownerDocument ?? document;
    const design = (step.design ?? {}) as Record<string, unknown>;

    // Z-index del host (contenedor de la guía) desde el diseño.
    if (typeof design.zIndex === 'number') host.style.zIndex = String(design.zIndex);

    // ── Card + diseño ──
    const card = doc.createElement('div');
    card.className = 'veo-card';
    applyCompositeDesign(card, design);

    const dismiss = (action: 'dismissed' | 'cta_clicked', meta?: StepBlock): void => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: idx,
        action,
        ...(meta?.id ? { metadata: { buttonId: meta.id } } : {}),
      });
    };

    // ── Contenido: recorre los bloques EN ORDEN (title/text/image/code/form
    // intercalados con filas de botones). Los form-blocks registran readers que
    // los botones submit-forms recogen. ──
    const readers: FormReader[] = [];
    this.buildContent(card, step.blocks ?? [], doc, context, idx, readers, host);
    // El último bloque en flujo NO debe dejar su margin-bottom colgando (title/
    // text/image traen uno por defecto para separar del siguiente; en el último
    // sobra y deja un hueco abajo). El ✕ todavía no se agregó, así que
    // `lastElementChild` es el último bloque real. Respeta un margen puesto a
    // mano (solo pisa el default del CSS, no un `style.marginBottom` explícito).
    const lastBlock = card.lastElementChild as HTMLElement | null;
    if (lastBlock && !lastBlock.style.marginBottom) lastBlock.style.marginBottom = '0';
    // En modo badge el ✕ solo cierra el TOOLTIP (el marcador es permanente). Se
    // asigna en `renderBadgeMode`; hasta entonces null (comportamiento normal).
    let closeTooltip: (() => void) | null = null;
    // ✕ de cierre (a menos que el diseño lo desactive), con color configurable.
    if (design.closeButton !== false) {
      const closeBtn = createCloseButton(doc, () => {
        if (closeTooltip) {
          closeTooltip();
          return;
        }
        dismiss('dismissed');
        this.closeOrSkip(context);
      });
      const closeColor = typeof design.closeColor === 'string' ? design.closeColor : null;
      const hoverColor = typeof design.closeHoverColor === 'string' ? design.closeHoverColor : null;
      if (closeColor) closeBtn.style.color = closeColor;
      // Posición del ✕: por defecto arriba-derecha, DENTRO de la tarjeta (el CSS
      // base lo deja a -6px, medio afuera y clavado en esa esquina). El usuario
      // lo puede mover a cualquiera de las 4 esquinas.
      const closePos =
        typeof design.closePosition === 'string' ? design.closePosition : 'top-right';
      applyClosePosition(closeBtn, closePos);
      // El hover del ✕ no se puede con inline CSS (:hover) → listeners que
      // intercambian el color (base ↔ hover).
      if (hoverColor) {
        closeBtn.addEventListener('mouseenter', () => {
          closeBtn.style.color = hoverColor;
        });
        closeBtn.addEventListener('mouseleave', () => {
          closeBtn.style.color = closeColor ?? '';
        });
      }
      card.appendChild(closeBtn);
    }

    // ── Modo BADGE: marcador permanente (bloques) anclado + tooltip (la card) ──
    if (badgeMode && anchor) {
      closeTooltip = this.renderBadgeMode(
        step,
        card,
        anchor,
        anchorSelector as string,
        host,
        root,
        doc,
        context,
        idx,
        design,
      );
      // El `shown` se emite al ABRIR el tooltip por primera vez (dentro del modo).
      return;
    }

    // ── Posicionamiento ──
    if (step.presentation === 'inline' && anchor) {
      host.style.display = 'block';
      const pos = inlinePosOf(step.inlinePosition);
      root.appendChild(card);
      insertHost(anchor, host, pos);
      this.registerCleanup(keepHostAttached(host, anchorSelector as string, pos));
    } else if (step.position === 'relative-to-element' && anchor) {
      // Backdrop opcional para el anclado (spotlight): oscurece la página y,
      // con targetAbove, eleva el elemento objetivo por encima del oscurecido.
      if (design.backdrop === true) {
        const bg = doc.createElement('div');
        bg.className = 'veo-composite-overlay veo-composite-overlay--dim';
        if (typeof design.backdropColor === 'string') bg.style.background = design.backdropColor;
        const bz = typeof design.backdropZIndex === 'number' ? design.backdropZIndex : 200000;
        bg.style.zIndex = String(bz);
        root.appendChild(bg);
        if (design.targetAbove === true) this.registerCleanup(liftTarget(anchor, bz + 1));
      }
      const wrapper = doc.createElement('div');
      wrapper.className = 'veo-composite-anchored';
      // Flecha (caret) que apunta al ancla, salvo que el diseño la desactive.
      const caretEl = design.caret === false ? null : makeCaret(doc, design);
      if (caretEl) wrapper.appendChild(caretEl);
      wrapper.appendChild(card);
      root.appendChild(wrapper);
      this.registerCleanup(await anchorPosition(anchor, wrapper, caretEl));
    } else {
      // Flotante en el viewport (centro o esquina). El fondo oscuro (backdrop)
      // se aplica según el toggle del usuario, INDEPENDIENTE de la posición:
      // una guía en una esquina también puede comportarse como modal (fondo
      // oscuro + click-fuera cierra). Antes solo se pintaba en 'center', así que
      // al mover la guía fuera del centro se perdía el backdrop.
      const overlay = doc.createElement('div');
      overlay.className = 'veo-composite-overlay';
      if (design.backdrop !== false) {
        overlay.classList.add('veo-composite-overlay--dim');
        if (typeof design.backdropColor === 'string') {
          overlay.style.background = design.backdropColor;
        }
        if (typeof design.backdropZIndex === 'number') {
          overlay.style.zIndex = String(design.backdropZIndex);
        }
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            dismiss('dismissed');
            this.closeOrSkip(context);
          }
        });
      }
      applyViewportPosition(card, step.position, design);
      overlay.appendChild(card);
      root.appendChild(overlay);
    }

    context.onInteraction({ guideId: context.guide.guideId, stepIndex: idx, action: 'shown' });
  }

  /** Recorre los bloques en orden y los pinta en la card. */
  private buildContent(
    card: HTMLElement,
    blocks: StepBlock[],
    doc: Document,
    context: GuideRenderContext,
    idx: number,
    readers: FormReader[],
    host: HTMLElement,
  ): void {
    const emit = (block: StepBlock): void => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: idx,
        action: 'cta_clicked',
        ...(block.id ? { metadata: { buttonId: block.id } } : {}),
      });
    };
    let i = 0;
    while (i < blocks.length) {
      const b = blocks[i];
      if (!b) {
        i++;
        continue;
      }
      if (b.type === 'button') {
        const row = doc.createElement('div');
        row.className = 'veo-guide-actions';
        // La alineación de un botón mueve la FILA (justify-content), no el botón
        // dentro. La toma del PRIMER botón del grupo.
        let rowAlign: string | undefined;
        while (i < blocks.length && blocks[i]?.type === 'button') {
          const bb = blocks[i] as StepBlock;
          if (!rowAlign) {
            const a = (bb.style as Record<string, unknown> | null | undefined)?.align;
            if (a === 'left' || a === 'center' || a === 'right') rowAlign = a;
          }
          row.appendChild(this.buildButton(bb, doc, context, idx, readers, emit));
          i++;
        }
        if (rowAlign) row.style.justifyContent = ROW_ALIGN[rowAlign] ?? 'flex-end';
        card.appendChild(row);
        continue;
      }
      if (b.type === 'code') {
        const { node, cleanup } = mountCodeBlock(b, doc, context, host);
        if (node) card.appendChild(node);
        this.registerCleanup(cleanup);
        i++;
        continue;
      }
      if (b.type === 'form') {
        const form = doc.createElement('div');
        form.className = 'veo-form';
        for (const field of b.fields ?? []) {
          const { wrapper, read } = buildField(field, doc);
          readers.push({ field, read });
          form.appendChild(wrapper);
        }
        card.appendChild(form);
        i++;
        continue;
      }
      const node = buildContentBlock(b, doc);
      if (node) card.appendChild(node);
      i++;
    }
  }

  /** Botón con enrutado de acción (navegación / cierre / url / guía / envío de forms). */
  private buildButton(
    block: StepBlock,
    doc: Document,
    context: GuideRenderContext,
    idx: number,
    readers: FormReader[],
    emit: (block: StepBlock) => void,
  ): HTMLButtonElement {
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'veo-guide-cta';
    btn.textContent = block.text?.trim() || 'OK';
    // Estilos por-bloque del botón (color/fondo/borde/tamaño/tipografía/…).
    applyBlockStyle(btn, block.style, 'button');
    const nav = context.nav?.callbacks;
    btn.addEventListener('click', () => {
      switch (block.action) {
        case 'next':
          emit(block);
          if (nav) nav.onNext();
          else context.onClose();
          break;
        case 'prev':
          emit(block);
          nav?.onBack();
          break;
        case 'url':
          emit(block);
          if (typeof block.url === 'string' && isSafeUrl(block.url)) {
            window.open(block.url, '_blank', 'noopener,noreferrer');
          }
          break;
        case 'guide':
          emit(block);
          if (block.targetGuideId) context.onOpenGuide?.(block.targetGuideId);
          this.closeOrSkip(context);
          break;
        case 'submit-forms':
        case 'submit-forms-advance':
        case 'submit-forms-goto':
          this.submitForms(readers, context, idx, btn, () => {
            emit(block);
            if (block.action === 'submit-forms' || !nav) {
              context.onClose();
            } else if (
              block.action === 'submit-forms-goto' &&
              typeof block.targetStep === 'number' &&
              nav.onGoto
            ) {
              nav.onGoto(block.targetStep);
            } else {
              nav.onNext();
            }
          });
          break;
        default:
          emit(block);
          this.closeOrSkip(context);
      }
    });
    return btn;
  }

  /** Recoge respuestas de todos los form-blocks del paso, valida y envía. */
  private submitForms(
    readers: FormReader[],
    context: GuideRenderContext,
    idx: number,
    btn: HTMLButtonElement,
    onDone: () => void,
  ): void {
    const answers: Record<string, unknown> = {};
    for (const { field, read } of readers) {
      const value = read();
      const empty = value === undefined || value === null || value === '';
      if (empty) {
        if (field.required) {
          btn.textContent = `Completa "${field.label}"`;
          return;
        }
        continue;
      }
      answers[field.key] = value;
    }
    if (readers.length > 0 && Object.keys(answers).length === 0) return;
    btn.disabled = true;
    const submitFn = context.onFormSubmit ?? (() => Promise.resolve(true));
    void submitFn(answers).then((ok) => {
      btn.disabled = false;
      if (!ok) return;
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: idx,
        action: 'completed',
      });
      onDone();
    });
  }

  private closeOrSkip(context: GuideRenderContext): void {
    if (context.nav?.callbacks) context.nav.callbacks.onSkip();
    else context.onClose();
  }

  /**
   * Modo BADGE: inserta un MARCADOR permanente (compuesto con `step.badge.blocks`)
   * junto al ancla, en el flujo del DOM. Al hover/click abre la `card` (los
   * bloques del paso) como TOOLTIP anclado al marcador. Devuelve una función que
   * cierra el tooltip (para el ✕). El marcador nunca se descarta.
   */
  private renderBadgeMode(
    step: CompositeStep,
    card: HTMLElement,
    anchor: Element,
    anchorSelector: string,
    host: HTMLElement,
    root: HTMLElement,
    doc: Document,
    context: GuideRenderContext,
    idx: number,
    design: Record<string, unknown>,
  ): () => void {
    const trigger = step.badge?.trigger === 'click' ? 'click' : 'hover';
    const placement =
      step.badge?.placement === 'top' ||
      step.badge?.placement === 'left' ||
      step.badge?.placement === 'right'
        ? step.badge.placement
        : 'bottom';

    // Marcador (badge): los bloques del usuario. Clickeable/hovereable.
    const badge = doc.createElement('div');
    badge.className = 'veo-composite-badge';
    badge.setAttribute('role', 'button');
    badge.tabIndex = 0;
    badge.style.cursor = 'pointer';
    badge.style.display = 'inline-flex';
    badge.style.alignItems = 'center';
    this.buildContent(badge, step.badge?.blocks ?? [], doc, context, idx, [], host);
    root.appendChild(badge);

    // Tooltip: la card (bloques del paso), oculta hasta el trigger, anclada al badge.
    // Un tooltip NO es ancho-completo (eso es para modales) → acotamos el ancho
    // para que se vea como un tooltip y la flecha pueda apuntar bien.
    if (design.fullWidth === true) {
      card.style.width = 'auto';
      card.style.maxWidth = '320px';
    } else if (!card.style.width && !card.style.maxWidth) {
      card.style.maxWidth = '320px';
    }
    const wrapper = doc.createElement('div');
    wrapper.className = 'veo-composite-anchored';
    wrapper.style.display = 'none';
    const caretEl = design.caret === false ? null : makeCaret(doc, design);
    if (caretEl) wrapper.appendChild(caretEl);
    wrapper.appendChild(card);
    root.appendChild(wrapper);

    // El host va al flujo, junto al ancla → el marcador queda permanente.
    host.style.display = 'inline-block';
    const pos = inlinePosOf(step.inlinePosition);
    insertHost(anchor, host, pos);
    this.registerCleanup(keepHostAttached(host, anchorSelector, pos));

    let open = false;
    let stopFloat: (() => void) | null = null;
    let closeTimer: number | null = null;
    let shownEmitted = false;

    const openTip = (): void => {
      if (open) return;
      open = true;
      wrapper.style.display = 'block';
      void anchorPosition(badge, wrapper, caretEl, placement).then((stop) => {
        if (open) stopFloat = stop;
        else stop(); // se cerró mientras posicionaba
      });
      if (!shownEmitted) {
        shownEmitted = true;
        context.onInteraction({ guideId: context.guide.guideId, stepIndex: idx, action: 'shown' });
      }
    };
    const closeTip = (): void => {
      open = false;
      wrapper.style.display = 'none';
      stopFloat?.();
      stopFloat = null;
    };

    if (trigger === 'hover') {
      const cancelClose = (): void => {
        if (closeTimer !== null) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      };
      const scheduleClose = (): void => {
        cancelClose();
        closeTimer = window.setTimeout(closeTip, 150);
      };
      badge.addEventListener('mouseenter', () => {
        cancelClose();
        openTip();
      });
      badge.addEventListener('mouseleave', scheduleClose);
      badge.addEventListener('focus', openTip);
      wrapper.addEventListener('mouseenter', cancelClose);
      wrapper.addEventListener('mouseleave', scheduleClose);
    } else {
      badge.addEventListener('click', () => {
        if (open) closeTip();
        else openTip();
      });
    }

    this.registerCleanup(() => {
      stopFloat?.();
      if (closeTimer !== null) clearTimeout(closeTimer);
    });

    return closeTip;
  }
}

function inlinePosOf(p: InlinePosition | null | undefined): InlinePosition {
  return p === 'before' || p === 'prepend' || p === 'append' ? p : 'after';
}

/** Fija la card en una esquina/centro del viewport. `hAlign` fuerza izq/der. */
function applyViewportPosition(
  card: HTMLElement,
  position: StepPosition,
  design: Record<string, unknown>,
): void {
  const s = card.style;
  s.position = 'fixed';
  const M = '16px';
  const hAlign = design.hAlign === 'left' ? 'left' : design.hAlign === 'right' ? 'right' : null;
  const v = position.includes('top') ? 'top' : position.includes('bottom') ? 'bottom' : 'middle';
  // hAlign (Alineación > Horizontal) gana sobre el eje horizontal de la posición.
  const h =
    hAlign ??
    (position.includes('left') ? 'left' : position.includes('right') ? 'right' : 'middle');

  if (v === 'top') s.top = M;
  else if (v === 'bottom') s.bottom = M;
  else s.top = '50%';

  if (h === 'left') s.left = M;
  else if (h === 'right') s.right = M;
  else s.left = '50%';

  // Transform para centrar el/los ejes que quedaron en 50%.
  const tx = h === 'middle' ? '-50%' : '0';
  const ty = v === 'middle' ? '-50%' : '0';
  s.transform = tx === '0' && ty === '0' ? '' : `translate(${tx}, ${ty})`;
}

/** Caret (flecha) que apunta al ancla, con el fondo/borde de la card. */
/** Espacio del ✕ respecto a la esquina elegida (dentro de la tarjeta). */
const CLOSE_INSET = '10px';

/**
 * Ubica el ✕ de cierre en una de las 4 esquinas, DENTRO de la tarjeta. Limpia
 * los `top/right` negativos del CSS base (que lo dejaban medio afuera).
 */
function applyClosePosition(btn: HTMLElement, position: string): void {
  const s = btn.style;
  s.top = 'auto';
  s.right = 'auto';
  s.bottom = 'auto';
  s.left = 'auto';
  const vertical = position.includes('bottom') ? 'bottom' : 'top';
  const horizontal = position.includes('left') ? 'left' : 'right';
  s.setProperty(vertical, CLOSE_INSET);
  s.setProperty(horizontal, CLOSE_INSET);
}

function makeCaret(doc: Document, design: Record<string, unknown>): HTMLElement {
  const caret = doc.createElement('div');
  caret.className = 'veo-composite-caret';
  const w = typeof design.caretWidth === 'number' ? design.caretWidth : 12;
  const h = typeof design.caretHeight === 'number' ? design.caretHeight : 12;
  const s = caret.style;
  s.position = 'absolute';
  s.width = `${w}px`;
  s.height = `${h}px`;
  s.transform = 'rotate(45deg)';
  s.background = typeof design.background === 'string' ? design.background : '#ffffff';
  if (typeof design.borderWidth === 'number' && design.borderWidth > 0) {
    const bc = typeof design.borderColor === 'string' ? design.borderColor : '#bbbbbb';
    s.border = `${design.borderWidth}px solid ${bc}`;
  }
  return caret;
}

/**
 * Eleva el elemento objetivo por encima del backdrop (targetAbove): lo resalta
 * dejándolo visible/clickeable sobre el oscurecido. Devuelve un restore.
 */
function liftTarget(anchor: Element, zIndex: number): () => void {
  const el = anchor as HTMLElement;
  const prevPosition = el.style.position;
  const prevZ = el.style.zIndex;
  if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
  el.style.zIndex = String(zIndex);
  return () => {
    el.style.position = prevPosition;
    el.style.zIndex = prevZ;
  };
}

/** Ancla la card a un elemento con Floating UI + reposiciona en scroll/resize. */
async function anchorPosition(
  anchor: Element,
  wrapper: HTMLElement,
  caret: HTMLElement | null,
  preferred: 'top' | 'bottom' | 'left' | 'right' = 'bottom',
): Promise<() => void> {
  const update = async (): Promise<void> => {
    const { x, y, placement, middlewareData } = await computePosition(anchor, wrapper, {
      placement: preferred,
      strategy: 'fixed',
      middleware: [
        offset(caret ? 10 : 8),
        flip(),
        shift({ padding: 8 }),
        ...(caret ? [arrow({ element: caret })] : []),
      ],
    });
    wrapper.style.left = `${x}px`;
    wrapper.style.top = `${y}px`;
    if (caret) positionArrow(caret, placement, middlewareData.arrow);
  };
  await update();
  const reposition = (): void => void update();
  window.addEventListener('scroll', reposition, true);
  window.addEventListener('resize', reposition);
  return () => {
    window.removeEventListener('scroll', reposition, true);
    window.removeEventListener('resize', reposition);
  };
}

/** Aplica el objeto `design` (font/border/bg/padding/margin) como estilos inline. */
function applyCompositeDesign(card: HTMLElement, design: Record<string, unknown>): void {
  const s = card.style;
  const px = (v: unknown): string | null =>
    typeof v === 'number' && Number.isFinite(v) ? `${v}px` : null;
  const strv = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

  if (strv(design.background)) s.background = design.background as string;
  // `color` (panel de estilos nuevo) con fallback `textColor` (guías viejas).
  const textColor = strv(design.color) ?? strv(design.textColor);
  if (textColor) s.color = textColor;
  if (px(design.fontSize)) s.fontSize = px(design.fontSize) as string;
  if (strv(design.fontWeight)) s.fontWeight = design.fontWeight as string;
  if (strv(design.fontFamily) && design.fontFamily !== 'inherit') {
    s.fontFamily = design.fontFamily as string;
  }
  if (strv(design.textTransform) && design.textTransform !== 'none') {
    s.textTransform = design.textTransform as string;
  }
  if (typeof design.letterSpacing === 'number') s.letterSpacing = `${design.letterSpacing}px`;
  if (strv(design.align)) s.textAlign = design.align as string;
  if (typeof design.lineHeight === 'number') s.lineHeight = `${design.lineHeight}%`;
  if (px(design.radius)) s.borderRadius = px(design.radius) as string;
  const bw = px(design.borderWidth);
  if (bw) s.border = `${bw} solid ${strv(design.borderColor) ?? '#e5e5e5'}`;
  // Sombra (X Y Blur Spread Color).
  if (strv(design.shadowColor) || typeof design.shadowBlur === 'number') {
    const n = (k: string) => (typeof design[k] === 'number' ? (design[k] as number) : 0);
    s.boxShadow = `${n('shadowX')}px ${n('shadowY')}px ${n('shadowBlur')}px ${n('shadowSpread')}px ${strv(design.shadowColor) ?? 'rgba(0,0,0,0.2)'}`;
  }
  // Dimensiones: ancho completo / fijo / fluido.
  if (design.fullWidth === true) {
    s.width = '100%';
  } else if (design.widthType === 'fluid') {
    s.width = 'auto';
    if (px(design.width)) s.maxWidth = px(design.width) as string;
  } else if (px(design.width)) {
    s.width = px(design.width) as string;
  }
  for (const side of ['Top', 'Right', 'Bottom', 'Left'] as const) {
    const p = px(design[`padding${side}`]);
    if (p) s.setProperty(`padding-${side.toLowerCase()}`, p);
    const m = px(design[`margin${side}`]);
    if (m) s.setProperty(`margin-${side.toLowerCase()}`, m);
  }
}
